import React, { useEffect, useState, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import {
    Button, Dropdown, Header, Icon, Image, Input,
    Label, List, Loader, Message, Segment
} from 'semantic-ui-react';
import { Link, useNavigate } from 'react-router-dom';
import { useStore } from '../../app/stores/store';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { agent } from '../../app/api/agent';
import debounce from 'lodash/debounce';
import { TeamSearchResultDto } from '../../app/models/Team';

// ── Type → colour map ──────────────────────────────────────────────────────────
const TYPE_COLOR: Record<string, 'yellow' | 'orange' | 'blue' | 'teal' | 'purple' | 'green'> = {
    ReceivedChallenge: 'yellow',
    SentChallenge:     'blue',
    OpenChallenge:     'purple',  // ← dodato
    TeamApplied:       'orange',
    ActiveDuel:        'teal',
    ScheduledMatch:    'green',
};

interface Props {
    /** Pre-selects a team in the "My team" dropdown (passed from TeamDashboard when
     *  the user clicks the Challenge shortcut button on a specific team card). */
    preselectedTeamId?: string;
}

export default observer(function ChallengesTab({ preselectedTeamId = '' }: Props) {
    const { teamStore, teamMatchStore } = useStore();
    const { t } = useTranslation();
    const navigate = useNavigate();

    // ── New-challenge form state ───────────────────────────────────────────────
    const [myTeamId,        setMyTeamId]        = useState('');
    const [searchQuery,     setSearchQuery]     = useState('');
    const [searchResults,   setSearchResults]   = useState<TeamSearchResultDto[]>([]);
    const [searching,       setSearching]       = useState(false);
    const [selectedOpponent, setSelectedOpponent] = useState<TeamSearchResultDto | null>(null);
    const [dropdownOpen,    setDropdownOpen]    = useState(false);
    const [mode,            setMode]            = useState<'direct' | 'open'>('direct');
    const [scheduledAt,     setScheduledAt]     = useState('');

    const [filterTeamId,    setFilterTeamId]    = useState<string | null>(null);

    // ── Bootstrap ─────────────────────────────────────────────────────────────
    useEffect(() => {
        teamMatchStore.loadPendingChallenges();
        teamStore.loadMyTeams();
    }, []);

    useEffect(() => {
        if (!preselectedTeamId) return;
        // Only accept if the team exists in eligible list
        const eligible = teamStore.myTeams.filter(t => t.status === 'Active' && t.isCurrentUserCaptain);
        const match = eligible.find(t => t.id === preselectedTeamId);
        if (match) {
            setMyTeamId(match.id);
            // Also pre-filter the history to that team so context is obvious
            setFilterTeamId(match.id);
        }
    }, [preselectedTeamId]);

    // Fallback: auto-select first eligible team if nothing is preselected yet
    useEffect(() => {
        if (myTeamId) return;                          // already set
        if (preselectedTeamId) return;                 // will be handled above once teams load
        const eligible = teamStore.myTeams.filter(t => t.status === 'Active' && t.isCurrentUserCaptain);
        if (eligible.length > 0) setMyTeamId(eligible[0].id);
    }, [teamStore.myTeams, myTeamId, preselectedTeamId]);

    // ── Debounced opponent search ──────────────────────────────────────────────
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const doSearch = useCallback(
        debounce(async (q: string, teamId: string) => {
            if (q.trim().length < 2 || !teamId) {
                setSearchResults([]);
                setDropdownOpen(false);
                setSearching(false);
                return;
            }
            setSearching(true);
            try {
                const results = await agent.TeamMatches.searchOpponents(teamId, q.trim());
                setSearchResults(results);
                setDropdownOpen(true);
            } catch {
                setSearchResults([]);
                setDropdownOpen(false);
            } finally {
                setSearching(false);
            }
        }, 350),
        []
    );

    const handleSearchChange = (value: string) => {
        setSearchQuery(value);
        if (selectedOpponent && value !== selectedOpponent.name) setSelectedOpponent(null);
        doSearch(value, myTeamId);
    };

    const handleSelectOpponent = (team: TeamSearchResultDto) => {
        setSelectedOpponent(team);
        setSearchQuery(team.name);
        setDropdownOpen(false);
        setSearchResults([]);
    };

    const handleClearOpponent = () => {
        setSelectedOpponent(null);
        setSearchQuery('');
        setSearchResults([]);
        setDropdownOpen(false);
    };

    const handleMyTeamChange = (teamId: string) => {
        setMyTeamId(teamId);
        setSelectedOpponent(null);
        setSearchQuery('');
        setSearchResults([]);
        setDropdownOpen(false);
    };

    // ── Submit ─────────────────────────────────────────────────────────────────
    const handleSend = async () => {
        if (!myTeamId) return;
        let matchId: string | null = null;

        if (mode === 'direct') {
            if (!selectedOpponent) return;
            matchId = await teamMatchStore.createDirectChallenge({
                teamAId: myTeamId,
                teamBId: selectedOpponent.id,
                scheduledAt: scheduledAt || undefined,
            });
        } else {
            matchId = await teamMatchStore.createOpenChallenge({
                teamAId: myTeamId,
                scheduledAt: scheduledAt || undefined,
            });
        }

        if (matchId) {
            setSelectedOpponent(null);
            setSearchQuery('');
            setScheduledAt('');
            navigate(`/teammatches/${matchId}`);
        }
    };

    // ── Derived ────────────────────────────────────────────────────────────────
    const eligibleMyTeams = teamStore.myTeams.filter(t => t.status === 'Active' && t.isCurrentUserCaptain);

    const myTeamOptions = eligibleMyTeams.map(t => ({
        key: t.id, value: t.id, text: t.name,
        image: { avatar: true, src: t.logoUrl || '/assets/team-placeholder.png' },
    }));

    const canSubmit =
        !!myTeamId &&
        !teamMatchStore.submitting &&
        (mode === 'open' || !!selectedOpponent);

    const allChallenges = teamMatchStore.pendingChallenges;

    const needsAction = allChallenges.filter(c =>
        c.type === 'ReceivedChallenge' || c.type === 'TeamApplied'
    );
    const pending = allChallenges.filter(c =>
        c.type === 'SentChallenge' || c.type === 'OpenChallenge'  // ← OpenChallenge dodat
    );
    const active = allChallenges.filter(c =>
        c.type === 'ActiveDuel' || c.type === 'ScheduledMatch'
    );

    const applyFilter = <T extends { challengerTeamId: string; challengedTeamId?: string | null }>(list: T[]): T[] => {
        if (!filterTeamId) return list;
        return list.filter(c => c.challengerTeamId === filterTeamId || c.challengedTeamId === filterTeamId);
    };

    // All my teams for the history filter dropdown
    const allMyTeamOptions = teamStore.myTeams.map(t => ({
        key: t.id, value: t.id, text: t.name,
        image: { avatar: true, src: t.logoUrl || '/assets/team-placeholder.png' },
    }));

    const isEmpty =
        !teamMatchStore.loadingPendingChallenges &&
        needsAction.length === 0 &&
        pending.length === 0 &&
        active.length === 0;

    // ── Render ─────────────────────────────────────────────────────────────────
    return (
        <div>
            {/* ═══════════════════════════════════════════════════════════════
                NEW CHALLENGE FORM
            ═══════════════════════════════════════════════════════════════ */}
            <Segment>
                <Header as="h4" style={{ marginBottom: 16 }}>
                    <Icon name="shield alternate" color="orange" />
                    <Header.Content>{t('challenges.newChallenge')}</Header.Content>
                </Header>

                {eligibleMyTeams.length === 0 ? (
                    <Message warning>
                        <Icon name="exclamation triangle" />
                        {t('challenges.noEligibleTeams')}
                    </Message>
                ) : (
                    <>
                        {/* Mode toggle */}
                        <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
                            <Button toggle active={mode === 'direct'} size="small"
                                color={mode === 'direct' ? 'teal' : undefined}
                                basic={mode !== 'direct'}
                                onClick={() => setMode('direct')}>
                                <Icon name="lock" />{t('teamMatch.createModal.direct')}
                            </Button>
                            <Button toggle active={mode === 'open'} size="small"
                                color={mode === 'open' ? 'blue' : undefined}
                                basic={mode !== 'open'}
                                onClick={() => setMode('open')}>
                                <Icon name="unlock" />{t('teamMatch.createModal.open')}
                            </Button>
                        </div>

                        {/* VS row */}
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>

                            {/* My team */}
                            <div style={{ flex: '1 1 160px', minWidth: 160 }}>
                                <label style={{ display: 'block', fontWeight: 700, marginBottom: 6, fontSize: '0.88em' }}>
                                    {t('challenges.myTeam')}
                                </label>
                                {eligibleMyTeams.length === 1 ? (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', background: '#f0fdf4', border: '1.5px solid #22c55e', borderRadius: 8 }}>
                                        <Image src={eligibleMyTeams[0].logoUrl || '/assets/team-placeholder.png'} avatar style={{ width: 28, height: 28, objectFit: 'cover' }} />
                                        <span style={{ fontWeight: 600, fontSize: '0.9em' }}>{eligibleMyTeams[0].name}</span>
                                        <Icon name="check circle" color="green" style={{ marginLeft: 'auto' }} />
                                    </div>
                                ) : (
                                    <Dropdown fluid selection
                                        options={myTeamOptions}
                                        value={myTeamId}
                                        onChange={(_, d) => handleMyTeamChange(d.value as string)}
                                        placeholder={t('challenges.selectMyTeam')}
                                    />
                                )}
                            </div>

                            {/* VS divider */}
                            <div style={{ display: 'flex', alignItems: 'center', paddingTop: 28, fontWeight: 900, fontSize: '1.1em', color: '#f97316' }}>
                                VS
                            </div>

                            {/* Opponent (direct only) */}
                            {mode === 'direct' && (
                                <div style={{ flex: '1 1 160px', minWidth: 160 }}>
                                    <label style={{ display: 'block', fontWeight: 700, marginBottom: 6, fontSize: '0.88em' }}>
                                        {t('challenges.opponentTeam')}
                                    </label>
                                    {selectedOpponent ? (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', background: '#f0fdf4', border: '1.5px solid #22c55e', borderRadius: 8 }}>
                                            <Image src={selectedOpponent.logoUrl || '/assets/team-placeholder.png'} avatar style={{ width: 28, height: 28, objectFit: 'cover', flexShrink: 0 }} />
                                            <span style={{ fontWeight: 600, fontSize: '0.9em', flex: 1 }}>{selectedOpponent.name}</span>
                                            <Button size="mini" basic icon="close" onClick={handleClearOpponent} style={{ padding: '4px 6px', flexShrink: 0 }} />
                                        </div>
                                    ) : (
                                        <div style={{ position: 'relative' }}>
                                            <Input fluid loading={searching}
                                                icon={searching ? undefined : 'search'}
                                                placeholder={myTeamId ? t('challenges.searchOpponentPlaceholder') : t('challenges.selectMyTeamFirst')}
                                                value={searchQuery}
                                                disabled={!myTeamId}
                                                onChange={e => handleSearchChange(e.target.value)}
                                                autoComplete="off"
                                            />
                                            {dropdownOpen && (
                                                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1000, background: '#fff', border: '1px solid #e0e0e0', borderTop: 'none', borderRadius: '0 0 8px 8px', boxShadow: '0 6px 20px rgba(0,0,0,0.1)', maxHeight: 240, overflowY: 'auto' }}>
                                                    {searching && (
                                                        <div style={{ padding: '12px 14px', textAlign: 'center', color: '#888', fontSize: '0.88em' }}>
                                                            <Loader active inline size="mini" style={{ marginRight: 8 }} />
                                                            {t('teamMatch.createModal.searchingTeams')}
                                                        </div>
                                                    )}
                                                    {!searching && searchResults.length === 0 && searchQuery.trim().length >= 2 && (
                                                        <div style={{ padding: '12px 14px', textAlign: 'center', color: '#aaa', fontSize: '0.88em' }}>
                                                            <Icon name="search" />{t('teamMatch.createModal.noTeamsFound')}
                                                        </div>
                                                    )}
                                                    {!searching && searchResults.map(team => (
                                                        <button key={team.id} onClick={() => handleSelectOpponent(team)}
                                                            style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '10px 14px', cursor: 'pointer', background: 'transparent', border: 'none', borderBottom: '1px solid #f5f5f5', textAlign: 'left', transition: 'background 0.12s' }}
                                                            onMouseEnter={e => (e.currentTarget.style.background = '#f5f8ff')}
                                                            onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                                                        >
                                                            <Image src={team.logoUrl || '/assets/team-placeholder.png'} avatar style={{ width: 32, height: 32, objectFit: 'cover', flexShrink: 0 }} />
                                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                                <div style={{ fontWeight: 600, fontSize: '0.9em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{team.name}</div>
                                                                <div style={{ fontSize: '0.75em', color: '#888', marginTop: 1 }}>
                                                                    <Icon name="user" size="small" />{team.memberCount} {t('teams.members')}
                                                                </div>
                                                            </div>
                                                            <Icon name="chevron right" color="grey" size="small" style={{ flexShrink: 0 }} />
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                            {!selectedOpponent && searchQuery.trim().length < 2 && myTeamId && (
                                                <div style={{ fontSize: '0.78em', color: '#aaa', marginTop: 4 }}>
                                                    <Icon name="info circle" />{t('teamMatch.createModal.typeToSearch')}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Open challenge placeholder */}
                            {mode === 'open' && (
                                <div style={{ flex: '1 1 160px', minWidth: 160 }}>
                                    <label style={{ display: 'block', fontWeight: 700, marginBottom: 6, fontSize: '0.88em' }}>
                                        {t('challenges.opponentTeam')}
                                    </label>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', background: '#eff6ff', border: '1px dashed #93c5fd', borderRadius: 8, color: '#3b82f6', fontSize: '0.88em' }}>
                                        <Icon name="question circle outline" />
                                        {t('challenges.openAnyOpponent')}
                                    </div>
                                </div>
                            )}
                        </div>

                        {mode === 'open' && (
                            <Message info size="small" style={{ marginBottom: 14 }}>
                                <Icon name="info circle" />{t('teamMatch.createModal.openHint')}
                            </Message>
                        )}

                        {/* Date + Send */}
                        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                            <div style={{ flex: '1 1 200px', minWidth: 180 }}>
                                <label style={{ display: 'block', fontWeight: 700, marginBottom: 6, fontSize: '0.88em' }}>
                                    {t('teamMatch.createModal.scheduledAt')}
                                    <span style={{ fontWeight: 400, color: '#aaa', marginLeft: 4 }}>({t('challenges.optional')})</span>
                                </label>
                                <Input type="datetime-local" value={scheduledAt}
                                    onChange={e => setScheduledAt(e.target.value)} fluid />
                            </div>
                            <Button color="orange" size="small"
                                loading={teamMatchStore.submitting}
                                disabled={!canSubmit}
                                onClick={handleSend}
                                style={{ marginBottom: 2 }}>
                                <Icon name="send" />
                                {mode === 'direct' ? t('teamMatch.createModal.sendChallenge') : t('teamMatch.createModal.publishChallenge')}
                            </Button>
                        </div>
                    </>
                )}
            </Segment>

            {/* ═══════════════════════════════════════════════════════════════
                CHALLENGES LIST
            ═══════════════════════════════════════════════════════════════ */}

            {teamMatchStore.loadingPendingChallenges && (
                <Segment basic textAlign="center">
                    <Loader active inline="centered" />
                </Segment>
            )}

            {/* Team filter row */}
            {!teamMatchStore.loadingPendingChallenges && !isEmpty && teamStore.myTeams.length > 1 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '0.88em', color: '#888' }}>{t('challenges.filterByTeam')}:</span>
                    <Dropdown
                        selection
                        clearable
                        options={allMyTeamOptions}
                        value={filterTeamId ?? ''}
                        onChange={(_, d) => setFilterTeamId((d.value as string) || null)}
                        placeholder={t('challenges.allTeams')}
                        style={{ minWidth: 180 }}
                    />
                    {filterTeamId && (
                        <Button size="mini" basic icon="close" content={t('challenges.clearFilter')}
                            onClick={() => setFilterTeamId(null)} />
                    )}
                </div>
            )}

            {/* Needs action */}
            {!teamMatchStore.loadingPendingChallenges && applyFilter(needsAction).length > 0 && (
                <Segment>
                    <Header as="h4" style={{ marginBottom: 12 }}>
                        <Icon name="exclamation circle" color="orange" />
                        <Header.Content>{t('challenges.needsAction')}</Header.Content>
                    </Header>
                    <List divided relaxed>
                        {applyFilter(needsAction).map(c => (
                            <ChallengeListItem key={c.matchId} challenge={c} t={t} />
                        ))}
                    </List>
                </Segment>
            )}

            {/* Sent + Open challenges (pending response) */}
            {!teamMatchStore.loadingPendingChallenges && applyFilter(pending).length > 0 && (
                <Segment>
                    <Header as="h4" style={{ marginBottom: 12 }}>
                        <Icon name="hourglass half" color="yellow" />
                        <Header.Content>{t('challenges.pendingTitle')}</Header.Content>
                    </Header>
                    <List divided relaxed>
                        {applyFilter(pending).map(c => (
                            <ChallengeListItem key={c.matchId} challenge={c} t={t} />
                        ))}
                    </List>
                </Segment>
            )}

            {/* Active Duels + Scheduled */}
            {!teamMatchStore.loadingPendingChallenges && applyFilter(active).length > 0 && (
                <Segment>
                    <Header as="h4" style={{ marginBottom: 12 }}>
                        <Icon name="handshake" color="teal" />
                        <Header.Content>{t('challenges.activeTitle')}</Header.Content>
                    </Header>
                    <List divided relaxed>
                        {applyFilter(active).map(c => (
                            <ChallengeListItem key={c.matchId} challenge={c} t={t} />
                        ))}
                    </List>
                </Segment>
            )}

            {/* Empty state */}
            {isEmpty && (
                <Message info>
                    <Icon name="shield alternate" />
                    {t('challenges.noChallenges')}
                </Message>
            )}

            {/* Filtered-out empty state */}
            {!isEmpty && filterTeamId &&
                applyFilter(needsAction).length === 0 &&
                applyFilter(pending).length === 0 &&
                applyFilter(active).length === 0 && (
                <Message info>
                    <Icon name="filter" />
                    {t('challenges.noMatchesForFilter')}
                    <Button size="mini" basic style={{ marginLeft: 10 }}
                        onClick={() => setFilterTeamId(null)}>
                        {t('challenges.clearFilter')}
                    </Button>
                </Message>
            )}
        </div>
    );
});

// ── Challenge list item ────────────────────────────────────────────────────────
interface ChallengeListItemProps {
    challenge: import('../../app/models/Team').PendingChallengeDto;
    t: (key: string, opts?: Record<string, unknown>) => string;
}

function ChallengeListItem({ challenge, t }: ChallengeListItemProps) {
    return (
        <List.Item
            as={Link}
            to={`/teammatches/${challenge.matchId}`}
            style={{ padding: '10px 0', cursor: 'pointer' }}
        >
            <List.Content>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <Image
                            src={challenge.challengerTeamLogoUrl || '/assets/team-placeholder.png'}
                            avatar style={{ width: 30, height: 30, objectFit: 'cover' }}
                        />
                        <div>
                            <div style={{ fontWeight: 600, fontSize: '0.92em' }}>
                                {challenge.challengerTeamName}
                                <span style={{ color: '#999', margin: '0 6px' }}>vs</span>
                                {challenge.challengedTeamName || (
                                    <span style={{ color: '#bbb', fontStyle: 'italic' }}>
                                        {t('challenges.openAnyOpponent')}
                                    </span>
                                )}
                            </div>
                            {challenge.scheduledAt && (
                                <div style={{ fontSize: '0.78em', color: '#888', marginTop: 2 }}>
                                    <Icon name="calendar" size="small" />
                                    {format(new Date(challenge.scheduledAt), 'dd MMM yyyy, HH:mm')}
                                </div>
                            )}
                            <div style={{ fontSize: '0.75em', color: '#aaa', marginTop: 1 }}>
                                <Icon name="clock" size="small" />
                                {format(new Date(challenge.sentAt), 'dd MMM yyyy')}
                            </div>
                        </div>
                    </div>
                    <Label size="small" color={TYPE_COLOR[challenge.type] ?? 'grey'}>
                        {t(`challenges.type.${challenge.type}`)}
                    </Label>
                </div>
            </List.Content>
        </List.Item>
    );
}